import { useState, type ReactElement } from "react";
import type { RdScheduleBlock } from "../../../../shared/rd-schedule";
import { WeekView, type WeekViewData } from "@/components/v2/week-view";
import {
  parseStoredWeekKeyOpen,
  serializeWeekKeyOpen,
  weekKeyStorageKey,
} from "@/lib/rd-schedule-week";

export interface AppointmentWeekPanelProps {
  /** The signed-in user, whose choice of the Key box is remembered. */
  userId: string | null;
  data: WeekViewData;
  onPreviousWeek: () => void;
  onNextWeek: () => void;
  onToday: () => void;
  onOpenDetails: (appointmentId: string) => void;
  /** The appointment whose details panel is open. */
  selectedId?: string | null;
  onOpenBlock?: (block: RdScheduleBlock) => void;
  onSetHours: (() => void) | null;
  onOpenWeek: (week: string) => void;
}

/**
 * NO-874: the Week view with the viewer's own choices. The missed filter
 * (on by default) lasts for the visit; the Key box (off by
 * default) is remembered per user in this browser, "0"/"1" only. Storage can
 * be blocked, so every read and write is guarded and the panel works without it.
 */
export function AppointmentWeekPanel(props: AppointmentWeekPanelProps): ReactElement {
  const [showMissed, setShowMissed] = useState(true);
  const [keyOpen, setKeyOpen] = useState(() => readKeyOpen(props.userId));

  return (
    <WeekView
      data={props.data}
      showMissed={showMissed}
      onShowMissedChange={setShowMissed}
      keyOpen={keyOpen}
      onKeyOpenChange={(open) => {
        setKeyOpen(open);
        writeKeyOpen(props.userId, open);
      }}
      onPreviousWeek={props.onPreviousWeek}
      onNextWeek={props.onNextWeek}
      onToday={props.onToday}
      onOpenDetails={props.onOpenDetails}
      selectedId={props.selectedId ?? null}
      onOpenBlock={props.onOpenBlock}
      onSetHours={props.onSetHours}
      onOpenWeek={props.onOpenWeek}
    />
  );
}

function readKeyOpen(userId: string | null): boolean {
  if (!userId) return false;

  try {
    return parseStoredWeekKeyOpen(
      window.localStorage.getItem(weekKeyStorageKey(userId))
    );
  } catch {
    return false;
  }
}

function writeKeyOpen(userId: string | null, open: boolean): void {
  if (!userId) return;

  try {
    window.localStorage.setItem(weekKeyStorageKey(userId), serializeWeekKeyOpen(open));
  } catch {
    // Blocked storage only loses the remembered choice.
  }
}
